import React, { useEffect, useMemo, useRef, useState } from "react";
import "../../src/assets/css/style.css";
import { useLocation } from "react-router-dom";
import {
  getCertificateDownload,
  getSignature,
} from "../CertificateService";
import { QRCodeCanvas } from "qrcode.react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

/**
 * Converts a raw Base64 string into a browser-compatible image source.
 * It also accepts existing data URLs and remote image URLs.
 */
const normalizeImageSource = (value, mimeType = "image/jpeg") => {
  if (!value || typeof value !== "string") {
    return "";
  }

  if (
    value.startsWith("data:") ||
    value.startsWith("http://") ||
    value.startsWith("https://")
  ) {
    return value;
  }

  return `data:${mimeType};base64,${value}`;
};

/**
 * Returns the appropriate Bengali or Nepali honorific.
 */
const getHonorific = (gender, isBengali) => {
  if (isBengali) {
    if (gender === "Mr.") return "শ্রী";
    if (gender === "Mrs.") return "শ্রীমতি";
    if (gender === "Miss") return "কুমারী";

    return "";
  }

  if (gender === "Mr.") return "श्री";
  if (gender === "Mrs.") return "श्रीमती";
  if (gender === "Miss") return "सुश्री";

  return "";
};

const BengaliCertificate = () => {
  const certificateRef = useRef(null);
  const location = useLocation();

  const applicationId = location.state?.applicationId;

  const [certificateDownloadData, setCertificateDownloadData] =
    useState(null);

  const [signatureData, setSignatureData] = useState({});
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState("");

  /**
   * Load certificate and signature information.
   */
  useEffect(() => {
    let isMounted = true;

    const loadCertificate = async () => {
      try {
        setError("");

        const certificateResult =
          await getCertificateDownload(applicationId);

        const response = certificateResult?.data;

        if (!isMounted) {
          return;
        }

        if (!response) {
          setError(
            "শংসাপত্রের তথ্য পাওয়া যায়নি। অনুগ্রহ করে আবার চেষ্টা করুন।"
          );
          return;
        }

        setCertificateDownloadData(response);

        if (response?.GpLGD) {
          try {
            const signatureResult = await getSignature(response.GpLGD);

            if (isMounted) {
              setSignatureData(signatureResult?.data || {});
            }
          } catch (signatureError) {
            console.error(
              "Unable to load certificate signature:",
              signatureError
            );

            if (isMounted) {
              setSignatureData({});
            }
          }
        }
      } catch (loadError) {
        console.error("Unable to load certificate:", loadError);

        if (isMounted) {
          setError(
            "শংসাপত্রটি লোড করা যায়নি। অনুগ্রহ করে আবার চেষ্টা করুন।"
          );
        }
      }
    };

    if (applicationId) {
      loadCertificate();
    } else {
      setError("Application ID পাওয়া যায়নি।");
    }

    return () => {
      isMounted = false;
    };
  }, [applicationId]);

  /**
   * OthLang B means Bengali.
   * Any other value uses the Nepali certificate.
   */
  const isBengali = certificateDownloadData?.OthLang === "B";

  const honorific = getHonorific(
    certificateDownloadData?.Gender,
    isBengali
  );

  /**
   * Certificate-specific statements.
   */
  const certificateStatements = useMemo(() => {
    if (!certificateDownloadData) {
      return [];
    }

    const response = certificateDownloadData;
    const personTitle = getHonorific(response?.Gender, isBengali);

    if (isBengali) {
      return [
        response?.UnmarriedCertificate > 0
          ? {
              key: "unmarried",
              content: (
                <>
                  আমার জানা মতে, <strong>{personTitle}</strong>{" "}
                  <strong>{response?.ApplicatNameBengali}</strong>{" "}
                  বর্তমানে অবিবাহিত।
                </>
              ),
            }
          : null,

        response?.CastCertificate > 0
          ? {
              key: "caste",
              content: (
                <>
                  <strong>{personTitle}</strong>{" "}
                  <strong>{response?.ApplicatNameBengali}</strong>,{" "}
                  <strong>{response?.SubCast}</strong> সম্প্রদায়ের
                  অন্তর্ভুক্ত, যা{" "}
                  <strong>{response?.Cast}</strong> বিভাগের অন্তর্গত।
                </>
              ),
            }
          : null,

        response?.IncomeCertificate > 0
          ? {
              key: "income",
              content: (
                <>
                  তার <strong>{response?.IncomeFinYear}</strong>{" "}
                  আর্থিক বছরের জন্য সকল উৎস থেকে মোট বার্ষিক
                  পারিবারিক আয় আনুমানিক{" "}
                  <strong>{response?.AnnualIncome}</strong>/- টাকা (
                  <strong>{response?.AnnualIncomeWoedBeng}</strong>)।
                </>
              ),
            }
          : null,

        response?.DistanceCertificate > 0
          ? {
              key: "distance",
              content: (
                <>
                  তার বাসস্থান এবং{" "}
                  <strong>{response?.ResidenceLocation}</strong>-এর
                  মধ্যে আনুমানিক দূরত্ব{" "}
                  <strong>{response?.ResidenceDistance}</strong>{" "}
                  কিলোমিটার।
                </>
              ),
            }
          : null,

        response?.SamePersonCertificate > 0
          ? {
              key: "same-person",
              content: (
                <>
                  <strong>{personTitle}</strong>{" "}
                  <strong>
                    {response?.SamePersonOldName ||
                      response?.ApplicatNameBengali}
                  </strong>{" "}
                  এবং <strong>{personTitle}</strong>{" "}
                  <strong>{response?.SamePersonNewName}</strong>{" "}
                  একই ব্যক্তি। তার{" "}
                  <strong>
                    {response?.RelationGurdianBengali}
                  </strong>{" "}
                  <strong>{response?.GurdianNameBengali}</strong>।
                  তার জন্ম তারিখ{" "}
                  <strong>{response?.DOBBengali}</strong>।
                </>
              ),
            }
          : null,

        response?.CharacterCertificate > 0
          ? {
              key: "character",
              content: (
                <>
                  <strong>{personTitle}</strong>{" "}
                  <strong>{response?.ApplicatNameBengali}</strong>{" "}
                  একজন সৎ, দায়িত্বশীল ও প্রশংসনীয় নৈতিক চরিত্রের
                  অধিকারী। ইহা আমার জ্ঞান ও বিশ্বাসমতে সত্য। আমি
                  তার জীবনে সাফল্য কামনা করি।
                </>
              ),
            }
          : null,
      ].filter(Boolean);
    }

    return [
      response?.UnmarriedCertificate > 0
        ? {
            key: "unmarried",
            content: (
              <>
                मेरो ज्ञान अनुसार,{" "}
                <strong>{personTitle}</strong>{" "}
                <strong>{response?.ApplicatNameBengali}</strong>{" "}
                अविवाहित छन्।
              </>
            ),
          }
        : null,

      response?.CastCertificate > 0
        ? {
            key: "caste",
            content: (
              <>
                उहाँ <strong>{response?.SubCast}</strong> समुदाय र{" "}
                <strong>{response?.Cast}</strong> वर्गमा
                पर्नुहुन्छ।
              </>
            ),
          }
        : null,

      response?.IncomeCertificate > 0
        ? {
            key: "income",
            content: (
              <>
                <strong>{response?.IncomeFinYear}</strong> आर्थिक
                वर्षका लागि सबै स्रोतबाट उहाँको कुल वार्षिक
                पारिवारिक आम्दानी करिब रु.{" "}
                <strong>{response?.AnnualIncome}</strong>/- (
                <strong>{response?.AnnualIncomeWord}</strong>) हो।
              </>
            ),
          }
        : null,

      response?.DistanceCertificate > 0
        ? {
            key: "distance",
            content: (
              <>
                उहाँको निवास र{" "}
                <strong>{response?.ResidenceLocation}</strong>{" "}
                बीचको अनुमानित दूरी{" "}
                <strong>{response?.ResidenceDistance}</strong>{" "}
                किलोमिटर छ।
              </>
            ),
          }
        : null,

      response?.SamePersonCertificate > 0
        ? {
            key: "same-person",
            content: (
              <>
                <strong>{personTitle}</strong>{" "}
                <strong>
                  {response?.SamePersonOldName ||
                    response?.ApplicatNameBengali}
                </strong>{" "}
                र <strong>{personTitle}</strong>{" "}
                <strong>{response?.SamePersonNewName}</strong>{" "}
                एउटै व्यक्ति हुन्। उहाँका{" "}
                <strong>
                  {response?.RelationGurdianBengali}
                </strong>{" "}
                <strong>{response?.GurdianNameBengali}</strong>{" "}
                हुनुहुन्छ। जन्म मिति{" "}
                <strong>{response?.DOBBengali}</strong> हो।
              </>
            ),
          }
        : null,

      response?.CharacterCertificate > 0
        ? {
            key: "character",
            content: (
              <>
                <strong>{personTitle}</strong>{" "}
                <strong>{response?.ApplicatNameBengali}</strong>{" "}
                इमानदार, जिम्मेवार र प्रशंसनीय नैतिक चरित्र भएका
                व्यक्ति हुनुहुन्छ। म उहाँको जीवनमा सफलताको कामना
                गर्दछु।
              </>
            ),
          }
        : null,
    ].filter(Boolean);
  }, [certificateDownloadData, isBengali]);

  /**
   * QR code information.
   */
  const qrData = useMemo(() => {
    if (!certificateDownloadData) {
      return "";
    }

    const response = certificateDownloadData;

    const appliedCertificates = [
      response?.CharacterCertificate > 0
        ? "Character Certificate"
        : null,

      response?.IncomeCertificate > 0
        ? "Income Certificate"
        : null,

      response?.ResidenceCertificate > 0
        ? "Residential Certificate"
        : null,

      response?.SamePersonCertificate > 0
        ? "Same Person Certificate"
        : null,

      response?.DistanceCertificate > 0
        ? "Distance Certificate"
        : null,

      response?.UnmarriedCertificate > 0
        ? "Unmarried Certificate"
        : null,

      response?.CastCertificate > 0
        ? "Caste Certificate"
        : null,
    ]
      .filter(Boolean)
      .join(", ");

    return JSON.stringify({
      "Applicant Name":
        response?.ApplicatNameBengali ||
        response?.ApplicantName ||
        "",

      "Application ID": response?.ApplicationId || "",

      "Applied Certificate": appliedCertificates,

      "Pradhan Name": response?.PradhanName || "",

      "Pradhan Designation": isBengali
        ? "প্রধান"
        : "प्रधान",

      "Gram Panchayat Name":
        response?.GPNameBeng || response?.GPName || "",

      "Block Name": isBengali
        ? response?.BlockNameBeng || ""
        : response?.BlockNameNepali ||
          response?.BlockNameBeng ||
          "",

      "District Name": isBengali
        ? response?.DistrictBeng || ""
        : response?.DistrictNepali ||
          response?.DistrictBeng ||
          "",

      "Valid Upto": response?.ValidDate || "",

      redirectUrl:
        "https://wbpms.in/citizen/#/certificate-check",
    });
  }, [certificateDownloadData, isBengali]);

  /**
   * Applicant image.
   */
  const applicantImageSrc = normalizeImageSource(
    certificateDownloadData?.ApplicantImageBase64,
    "image/jpeg"
  );

  /**
   * Officer signature image.
   */
  const signatureImageSrc = normalizeImageSource(
    signatureData?.officerSignature,
    "image/png"
  );

  /**
   * Generate an A4 PDF.
   */
  const handleDownload = async () => {
    if (!certificateRef.current || isDownloading) {
      return;
    }

    try {
      setIsDownloading(true);
      setError("");

      if (document.fonts?.ready) {
        await document.fonts.ready;
      }

      const canvas = await html2canvas(
        certificateRef.current,
        {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          backgroundColor: "#ffffff",
          logging: false,
          scrollX: 0,
          scrollY: -window.scrollY,
        }
      );

      const imageData = canvas.toDataURL(
        "image/jpeg",
        0.96
      );

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: "a4",
        compress: true,
      });

      const pageWidth =
        pdf.internal.pageSize.getWidth();

      const pageHeight =
        pdf.internal.pageSize.getHeight();

      pdf.addImage(
        imageData,
        "JPEG",
        0,
        0,
        pageWidth,
        pageHeight,
        undefined,
        "MEDIUM"
      );

      pdf.save(
        `${
          certificateDownloadData?.ApplicationId ||
          "certificate"
        }.pdf`
      );
    } catch (downloadError) {
      console.error(
        "Error generating PDF:",
        downloadError
      );

      setError(
        isBengali
          ? "PDF তৈরি করা যায়নি। অনুগ্রহ করে আবার চেষ্টা করুন।"
          : "PDF बनाउन सकिएन। कृपया पुनः प्रयास गर्नुहोस्।"
      );
    } finally {
      setIsDownloading(false);
    }
  };

  /**
   * Error screen.
   */
  if (error && !certificateDownloadData) {
    return (
      <div className="english-cert-message english-cert-error">
        {error}
      </div>
    );
  }

  /**
   * Loading screen.
   */
  if (!certificateDownloadData) {
    return (
      <div className="english-cert-message">
        Loading certificate...
      </div>
    );
  }

  /**
   * Reduce spacing when many certificate statements are active.
   */
  const compactClass =
    certificateStatements.length >= 5
      ? "english-cert-body--compact"
      : "";

  const gpName =
    certificateDownloadData?.GPNameBeng ||
    certificateDownloadData?.GPName ||
    "";

  const gpAddress =
    signatureData?.gpAddressbeng ||
    certificateDownloadData?.GPAddress ||
    "";

  const blockName = isBengali
    ? certificateDownloadData?.BlockNameBeng ||
      certificateDownloadData?.BlockName ||
      ""
    : certificateDownloadData?.BlockNameNepali ||
      certificateDownloadData?.BlockNameBeng ||
      certificateDownloadData?.BlockName ||
      "";

  const districtName = isBengali
    ? certificateDownloadData?.DistrictBeng ||
      certificateDownloadData?.District ||
      ""
    : certificateDownloadData?.DistrictNepali ||
      certificateDownloadData?.DistrictBeng ||
      certificateDownloadData?.District ||
      "";

  const applicantName =
    certificateDownloadData?.ApplicatNameBengali ||
    certificateDownloadData?.ApplicantName ||
    "";

  const guardianRelation =
    certificateDownloadData?.RelationGurdianBengali ||
    certificateDownloadData?.RelationGurdian ||
    "";

  const guardianName =
    certificateDownloadData?.GurdianNameBengali ||
    certificateDownloadData?.GurdianName ||
    "";

  const applicantAddress =
    certificateDownloadData?.AddressBengali ||
    certificateDownloadData?.Address ||
    "";

  const postOffice =
    certificateDownloadData?.PostOfficeBengali ||
    certificateDownloadData?.PostOffice ||
    "";

  const policeStation =
    certificateDownloadData?.PoliceStationBengali ||
    certificateDownloadData?.PoliceStation ||
    "";

  const pin =
    certificateDownloadData?.PinBengali ||
    certificateDownloadData?.Pin ||
    "";

  const officerName = isBengali
    ? signatureData?.officerNameBeng ||
      signatureData?.officerName ||
      ""
    : signatureData?.officerNameNepali ||
      signatureData?.officerNameBeng ||
      signatureData?.officerName ||
      "";

  const officerDesignation = isBengali
    ? signatureData?.officerDesigBeng ||
      signatureData?.officerDesig ||
      certificateDownloadData?.PradhanDesig ||
      "প্রধান"
    : signatureData?.officerDesigNepali ||
      signatureData?.officerDesig ||
      "प्रधान";

  return (
    <div className="english-cert-page bengali-cert-page">
      <div className="english-cert-preview">
        <section
          ref={certificateRef}
          className="english-certificate-sheet bengali-certificate-sheet"
          lang={isBengali ? "bn" : "ne"}
        >
          <div className="english-cert-safe-area">
            {/* Certificate heading */}
            <header className="english-cert-header">
              <h1>{gpName}</h1>
              <p>{gpAddress}</p>
            </header>

            {/* Certificate number and date */}
            <div className="english-cert-meta">
              <p>
                {isBengali
                  ? "সার্টিফিকেট নম্বর"
                  : "प्रमाणपत्र नम्बर"}
                :{" "}
                <strong>
                  {certificateDownloadData?.ApplicationId}
                </strong>
              </p>

              <p>
                {isBengali ? "তারিখ" : "मिति"}:{" "}
                <strong>
                  {certificateDownloadData?.ApproveDate}
                </strong>
              </p>
            </div>

            {/* Certificate title */}
            <div className="english-cert-title-wrap">
              <h2 className="english-cert-title">
                TO WHOM IT MAY CONCERN
              </h2>
            </div>

            <main
              className={`english-cert-body ${compactClass}`}
            >
              {/* Applicant information */}
              <section className="english-cert-applicant-block">
                <div className="english-cert-applicant-copy">
                  {isBengali ? (
                    <>
                      <p>
                        এই মর্মে শংসাপত্র দেওয়া হচ্ছে যে,{" "}
                        <strong>{honorific}</strong>{" "}
                        <strong>{applicantName}</strong>,{" "}
                        <strong>{guardianRelation}</strong>:{" "}
                        <strong>{guardianName}</strong>,{" "}
                        <strong>{applicantAddress}</strong>,
                        পোস্ট অফিস-{" "}
                        <strong>{postOffice}</strong>, থানা-{" "}
                        <strong>{policeStation}</strong>,
                        জেলা- <strong>{districtName}</strong>,
                        পিন- <strong>{pin}</strong>-এ
                        স্থায়ীভাবে বসবাসকারী এবং আমার
                        সুপরিচিত।
                      </p>

                      <p>
                        <strong>{honorific}</strong>{" "}
                        <strong>{applicantName}</strong>{" "}
                        <strong>{applicantAddress}</strong>,
                        পোস্ট অফিস-{" "}
                        <strong>{postOffice}</strong>, থানা-{" "}
                        <strong>{policeStation}</strong>,
                        জেলা- <strong>{districtName}</strong>,
                        পিন- <strong>{pin}</strong>,
                        পশ্চিমবঙ্গের স্থায়ী বাসিন্দা।
                      </p>
                    </>
                  ) : (
                    <>
                      <p>
                        यसद्वारा प्रमाणित गरिन्छ कि,{" "}
                        <strong>{honorific}</strong>{" "}
                        <strong>{applicantName}</strong>,{" "}
                        <strong>{guardianRelation}</strong>:{" "}
                        <strong>{guardianName}</strong>,{" "}
                        <strong>{applicantAddress}</strong>,
                        हुलाक कार्यालय-{" "}
                        <strong>{postOffice}</strong>,
                        प्रहरी स्टेशन-{" "}
                        <strong>{policeStation}</strong>,
                        जिल्ला-{" "}
                        <strong>{districtName}</strong>, पिन-{" "}
                        <strong>{pin}</strong>, पश्चिम
                        बङ्गालका स्थायी बासिन्दा हुनुहुन्छ
                        र म उहाँलाई राम्रोसँग चिन्दछु।
                      </p>

                      <p>
                        <strong>{honorific}</strong>{" "}
                        <strong>{applicantName}</strong>{" "}
                        <strong>{applicantAddress}</strong>,
                        हुलाक कार्यालय-{" "}
                        <strong>{postOffice}</strong>,
                        प्रहरी स्टेशन-{" "}
                        <strong>{policeStation}</strong>,
                        जिल्ला-{" "}
                        <strong>{districtName}</strong>, पिन-{" "}
                        <strong>{pin}</strong>, पश्चिम
                        बङ्गालका स्थायी बासिन्दा हुनुहुन्छ।
                      </p>
                    </>
                  )}
                </div>

                {/* Applicant photograph */}
                {applicantImageSrc && (
                  <div className="english-cert-photo-column">
                    <div className="english-cert-photo">
                      <img
                        src={applicantImageSrc}
                        alt="Certificate applicant"
                        crossOrigin="anonymous"
                      />
                    </div>
                  </div>
                )}
              </section>

              {/* Dynamic certificate statements */}
              {certificateStatements.length > 0 && (
                <section className="english-cert-declaration-block">
                  {certificateStatements.map(
                    (statement) => (
                      <p key={statement.key}>
                        {statement.content}
                      </p>
                    )
                  )}
                </section>
              )}

              {/* QR and authorised signature */}
              <section className="english-cert-auth-block">
                <div className="english-cert-verification">
                  <div className="english-cert-qr-frame">
                    <QRCodeCanvas
                      value={qrData}
                      size={108}
                      level="M"
                      includeMargin={false}
                    />
                  </div>

                  <div className="english-cert-verification-copy">
                    <strong>
                      {isBengali
                        ? "ডিজিটাল যাচাইকরণ"
                        : "डिजिटल प्रमाणीकरण"}
                    </strong>

                    <span>
                      {isBengali
                        ? "শংসাপত্র যাচাই করতে স্ক্যান করুন"
                        : "प्रमाणपत्र जाँच गर्न स्क्यान गर्नुहोस्"}
                    </span>

                    <small>https://wbpms.in</small>

                    <small>
                      {isBengali
                        ? "শংসাপত্রটি ৬ মাসের জন্য বৈধ"
                        : "प्रमाणपत्र ६ महिनासम्म मान्य छ"}
                    </small>
                  </div>
                </div>

                <div className="english-cert-authority">
                  <div className="english-cert-signature-area">
                    {signatureImageSrc && (
                      <img
                        src={signatureImageSrc}
                        alt="Authorised signature"
                        crossOrigin="anonymous"
                      />
                    )}
                  </div>

                  <div className="english-cert-authority-line" />

                  <p className="english-cert-authority-name">
                    {officerName}
                  </p>

                  <p>{officerDesignation}</p>

                  <p>{gpName}</p>

                  <p>
                    {blockName}
                    {blockName && districtName ? ", " : ""}
                    {districtName}
                  </p>
                </div>
              </section>
            </main>

            {/* Certificate verification footer */}
            <footer className="english-cert-footer">
              <em>
                To verify the authenticity of this
                certificate, please visit
                https://wbpms.in/citizen/#/certificate-check
              </em>
            </footer>
          </div>
        </section>
      </div>

      {/* PDF generation error */}
      {error && (
        <div className="english-cert-inline-error">
          {error}
        </div>
      )}

      {/* Download button */}
      <div className="english-cert-actions">
        <button
          type="button"
          onClick={handleDownload}
          className="english-cert-download-button"
          disabled={isDownloading}
        >
          {isDownloading
            ? "Generating PDF..."
            : "Download Certificate"}
        </button>
      </div>
    </div>
  );
};

export default BengaliCertificate;